import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { ProcessingShowcase } from '../../../../components/flow/ProcessingShowcase';
import { discoveryStep } from '../../../../components/flow/stageToStep';
import { DiscoveryQueryKeys, DiscoveryStatuses } from '../api';
import { DiscoveryProgressBar } from '../components/DiscoveryProgressBar';
import { useDraftPolling } from '../hooks';

/**
 * Step 2 — polling phase. While the draft status is `Analyzing` we refetch
 * every 2 seconds (handled by `useDraftPolling`). The orchestrator publishes
 * per-step progress to the draft so the user sees a live progress bar plus a
 * step label.
 *
 * On `Failed` we render an error card with the backend `failureReason` and a
 * "Volver a empezar" button so the user gets explicit context (the silent
 * navigate-on-Failed branch from Wave 11.F left the user without any signal).
 */
export function DiscoveryAnalyzingStep(): JSX.Element {
  const { draftId } = useParams<{ draftId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useDraftPolling(draftId);

  useEffect(() => {
    if (data === undefined || draftId === undefined) {
      return;
    }
    if (data.status === DiscoveryStatuses.Analyzing) {
      return;
    }
    // Don't auto-navigate on Failed anymore — we render the failure card below
    // with the explicit reason and a CTA so the user controls the next step.
    if (data.status === DiscoveryStatuses.Failed) {
      return;
    }
    // The Review step shares the same query key as the polling hook. If we
    // navigate in the same tick that the status flipped, Review can mount
    // reading a stale snapshot with candidateFields = []. Invalidate first
    // so Review fetches a guaranteed-fresh draft on mount.
    const go = async (): Promise<void> => {
      await queryClient.invalidateQueries({ queryKey: DiscoveryQueryKeys.draft(draftId) });
      if (data.status === DiscoveryStatuses.Published) {
        navigate(
          data.publishedDocumentTypeSlug !== undefined && data.publishedDocumentTypeSlug !== null
            ? `/document-types/${encodeURIComponent(data.publishedDocumentTypeSlug)}`
            : '/document-types',
        );
        return;
      }
      // A draft still gathering input has nothing to review — send it back to upload instead of
      // the review empty-state dead-end (defense-in-depth on top of the add-sample cache seed).
      if (data.status === DiscoveryStatuses.Created) {
        navigate(`/document-types/discover/${encodeURIComponent(draftId)}/upload`);
        return;
      }
      navigate(`/document-types/discover/${encodeURIComponent(draftId)}/review`);
    };
    void go();
  }, [data, draftId, navigate, queryClient]);

  if (isError) {
    const message = error instanceof Error ? error.message : 'Error desconocido.';
    return (
      <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
        <strong className="font-semibold">Error al consultar la propuesta de tipo.</strong>
        <p className="mt-1">{message}</p>
      </div>
    );
  }

  if (data !== undefined && data.status === DiscoveryStatuses.Failed) {
    const reason =
      data.failureReason !== undefined && data.failureReason !== null && data.failureReason.length > 0
        ? data.failureReason
        : 'El proceso de análisis no pudo completarse.';
    return (
      <div
        className="card border-rose-200 bg-rose-50 p-6 text-sm text-rose-900"
        data-testid="discovery-failed-card"
      >
        <h2 className="text-lg font-semibold text-rose-800">No se pudo completar el análisis</h2>
        <p className="mt-2" data-testid="discovery-failure-reason">
          {reason}
        </p>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            className="btn-primary"
            onClick={() => navigate('/document-types/discover/upload')}
            data-testid="discovery-retry-button"
          >
            Volver a empezar
          </button>
        </div>
      </div>
    );
  }

  const lastMessage = data?.progress.lastMessage;
  // Only show the animation once we know the draft is actually analyzing — avoids
  // a flash on mount / while the first poll is in flight.
  const isAnalyzing = data !== undefined && data.status === DiscoveryStatuses.Analyzing;
  return (
    <div className="space-y-4">
      {isAnalyzing ? (
        <ProcessingShowcase
          variant="discovery"
          step={discoveryStep(data.progress.currentStep)}
          caption={lastMessage !== undefined && lastMessage !== null && lastMessage.length > 0
            ? lastMessage
            : undefined}
        />
      ) : null}
      <div className="card flex flex-col items-stretch gap-4 p-6">
        <h2 className="text-base font-semibold text-slate-900">Analizando muestras…</h2>
        {data !== undefined ? (
          <DiscoveryProgressBar
            percentComplete={data.progress.percentComplete}
            currentStep={data.progress.currentStep}
            lastMessage={data.progress.lastMessage}
          />
        ) : null}
        <p className="text-sm text-slate-500">
          {data === undefined || isLoading
            ? 'Recuperando el estado del descubrimiento.'
            : `Procesadas ${data.processedSampleCount} / ${data.sampleCount} muestras.`}
        </p>
        <p className="text-xs text-slate-400">
          Esta página se actualiza automáticamente cada 2 segundos.
        </p>
      </div>
    </div>
  );
}
